/**
 * The dictionary the language currently on screen is using.
 *
 * This is deliberately a leaf module with no React in it and no directory import,
 * because two plain modules need to read it: `lib/api.ts`, which builds the sentence
 * a failed request reports, and `auth/desktopHandOff.ts`, which builds the sentence a
 * refused sign-in reports.
 *
 * Both are ordinary modules that the test runner imports directly, with no bundler in
 * front of them. When the store lived in `i18n/index.tsx` they pulled in a directory
 * that ESM cannot resolve on its own, and `desktopHandOff.test.ts` stopped running at
 * all. The rule this file exists to satisfy: a module that has to work without a
 * bundler must only import from modules that do too.
 *
 * ## When it is written, and why that matters
 *
 * The write is in an effect, so it happens after commit rather than during render.
 * That is deliberate: writing during render would be the genuinely dangerous choice
 * here, because under concurrent rendering an abandoned render leaves the locator
 * pointing at a language that never reached the screen. `StrictMode` double-invoking
 * the effect costs nothing, because it has no cleanup and writes the same value again.
 *
 * The price is a window on the first paint where this still holds the previous
 * language. Nothing may therefore resolve a sentence *through* this during render.
 * `ApiError` carries a key and resolves it when read for exactly this reason.
 *
 * The alternative, threading a dictionary into every fetch, would put a language
 * argument on all of them for the sake of one sentence, and would have to be done
 * again on every endpoint added later.
 */

import { ptBR } from './pt-BR.ts';
import type { Dictionary } from './types.ts';

let active: Dictionary = ptBR;

/**
 * The dictionary for the language currently rendered.
 *
 * Starts as Portuguese, which is the right default for two reasons: it is what the
 * unprefixed address bar means, and it is what a test with no provider gets. A test
 * asserting on a phrase is therefore asserting on the phrase the default language
 * produces, with no setup.
 */
export function activeDictionary(): Dictionary {
  return active;
}

/** Written by `<LocaleHead>` during render. Not called by anything else. */
export function setActiveDictionary(dictionary: Dictionary): void {
  active = dictionary;
}